/**
 * Featured Artworks Block
 * Curated artwork gallery carousel
 */
import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { Palette, Sparkles, Heart } from 'lucide-react';
import { Badge } from '../ui/badge';
import { ScrollContainer, SectionHeader, resolveImageUrl } from './shared';

const FeaturedArtworksBlock = ({ data = [], settings = {} }) => {
  const scrollRef = useRef(null);
  const artworks = data || [];
  
  const {
    title = 'Featured Artworks',
    max_items = 12,
    card_scale = 1.0,
    max_image_height = 300,
  } = settings;

  if (artworks.length === 0) return null;

  const displayArtworks = artworks.slice(0, max_items);
  const cardWidth = Math.round(192 * card_scale); // base w-48 = 192px

  return (
    <section className="mb-6 sm:mb-12" data-testid="featured-artworks-block">
      <SectionHeader 
        title={title}
        icon={Palette}
        iconColor="text-pink-400"
        viewAllLink="/gallery"
        viewAllText="View Gallery →"
      />
      
      <ScrollContainer scrollRef={scrollRef}>
        {displayArtworks.map((artwork) => (
          <Link
            key={artwork.artwork_id}
            to={`/art/${artwork.artwork_id}`}
            className="flex-shrink-0 group"
            style={{ width: `${cardWidth}px` }}
            data-testid={`featured-artwork-${artwork.artwork_id}`}
          >
            <div 
              className="relative rounded-lg overflow-hidden bg-zinc-900 border border-zinc-800 group-hover:border-pink-500/50 transition-all duration-300"
              style={{ height: `${max_image_height}px` }}
            >
              <img
                src={resolveImageUrl(artwork.thumb_url || artwork.thumbnail_url || artwork.image_url)}
                alt={artwork.title || 'Featured artwork'}
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              />
              {/* Gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              {/* Featured badge */}
              <div className="absolute top-2 left-2">
                <Badge className="bg-gradient-to-r from-pink-500 to-purple-500 text-white text-xs px-2 py-0.5 shadow-lg">
                  <Sparkles className="w-3 h-3 mr-1" />
                  Featured
                </Badge>
              </div>
              {/* Likes indicator */}
              {artwork.likes > 0 && (
                <div className="absolute top-2 right-2 flex items-center gap-1 bg-black/60 rounded-full px-2 py-0.5">
                  <Heart className="w-3 h-3 text-red-400 fill-red-400" />
                  <span className="text-xs text-white">{artwork.likes}</span>
                </div>
              )}
              {/* Title overlay on hover */}
              <div className="absolute bottom-0 left-0 right-0 p-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <p className="font-semibold text-white text-sm truncate">
                  {artwork.title || 'Untitled'}
                </p>
                {artwork.creator_handle && (
                  <p className="text-xs text-zinc-400">@{artwork.creator_handle}</p>
                )}
              </div>
            </div>
            {/* Info below image */}
            <div className="mt-2 px-1">
              <p className="font-medium text-white text-sm truncate group-hover:text-pink-400 transition-colors">
                {artwork.title || 'Untitled'}
              </p>
              {artwork.creator_display_name && (
                <p className="text-xs text-zinc-500 truncate">by {artwork.creator_display_name}</p>
              )}
            </div>
          </Link>
        ))}
      </ScrollContainer>
    </section>
  );
};

export default FeaturedArtworksBlock;
